<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import {
	buildVisibleCategories,
	buildWheelSlices,
	repeatSlotCategories,
	selectorDurations,
	slotTargetIndex,
	wheelTargetRotation,
	type SelectorCategory,
} from '../categorySelector';
import type { NewTriviaSettings, SelectorMode, Theme } from '../types';

interface Labels {
	answerQuestion: string;
	category: string;
	flipCardHelp: string;
	flipCardTitle: string;
	pullCategoryHelp: string;
	pullCategoryTitle: string;
	revealQuestion: string;
	spin: string;
	spinCategoryHelp: string;
	spinCategoryTitle: string;
	tapSpinCategory: string;
}

const props = defineProps<{
	mode: SelectorMode;
	target: Omit<SelectorCategory, 'key' | 'target'>;
	categories: Array<Omit<SelectorCategory, 'key' | 'target'>>;
	settings: NewTriviaSettings['categorySelectorSettings'];
	theme: Theme;
	logoUrl: string;
	gameTitle: string;
	labels: Labels;
}>();

const emit = defineEmits<{ reveal: [category: string] }>();
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const durations = selectorDurations(reducedMotion);
const visibleCategories = ref<SelectorCategory[]>([]);
const selectedCard = ref<number | null>(null);
const cardFlipped = ref(false);
const cardReady = ref(false);
const spinning = ref(false);
const settling = ref(false);
const wheelRotation = ref(0);
const slotIndex = ref(0);
const slotLocked = ref(false);
const revealButton = ref<HTMLButtonElement | null>(null);
const timeoutIds: number[] = [];

const later = (callback: () => void, delay: number): void => {
	timeoutIds.push(window.setTimeout(callback, delay));
};

const clearTimers = (): void => {
	timeoutIds.splice(0).forEach((id) => window.clearTimeout(id));
};

const reset = (): void => {
	clearTimers();
	const categories = buildVisibleCategories(props.target, props.categories, props.mode);
	visibleCategories.value = props.mode === 'wheel'
		? categories.map((category, index) => ({
			...category,
			color: props.settings.wheelColors[index % props.settings.wheelColors.length] || category.color,
		}))
		: categories;
	selectedCard.value = null;
	cardFlipped.value = false;
	cardReady.value = false;
	spinning.value = false;
	settling.value = false;
	wheelRotation.value = 0;
	slotIndex.value = visibleCategories.value.length;
	slotLocked.value = false;
};

watch(
	() => [
		props.target.name,
		props.mode,
		props.categories.map((category) => category.name).join('|'),
		props.settings.wheelColors.join('|'),
	],
	reset,
	{ immediate: true },
);

const wheelSlices = computed(() => buildWheelSlices(visibleCategories.value));
const slotRows = computed(() => repeatSlotCategories(visibleCategories.value));
const slotTransform = computed(() => `translateY(${74 - slotIndex.value * 74}px)`);
const slotTransition = computed(() => spinning.value
	? `transform ${durations.slot}ms cubic-bezier(.12, .68, .08, 1)`
	: 'none');
const cardBackStyle = computed<Record<string, string>>(() => ({
	backgroundColor: props.settings.cardColor,
	...(props.settings.cardImageUrl ? {
		backgroundImage: `url("${props.settings.cardImageUrl.replace(/["\\]/g, '')}")`,
	} : {}),
}));
const cardFrontStyle = computed<Record<string, string>>(() => ({
	background: `linear-gradient(145deg, ${props.target.color}, ${props.theme.textColor})`,
	color: props.theme.categoryLabelText,
}));
const slotStageStyle = computed(() => ({
	backgroundColor: props.settings.slotMachineColor,
	borderColor: props.settings.slotBarrelColor,
}));
const slotWindowStyle = computed(() => ({ borderColor: props.settings.slotBarrelColor }));

const chooseCard = (index: number): void => {
	if( selectedCard.value !== null ) return;
	selectedCard.value = index;
	later(() => { cardFlipped.value = true; }, durations.cardFlip);
	later(() => {
		cardReady.value = true;
		nextTick(() => revealButton.value?.focus());
	}, durations.cardReady);
};

const spinWheel = (): void => {
	if( spinning.value ) return;
	spinning.value = true;
	const targetIndex = Math.max(0, visibleCategories.value.findIndex((category) => category.target));
	const nextRotation = wheelTargetRotation(targetIndex, visibleCategories.value.length, wheelRotation.value, reducedMotion);
	window.requestAnimationFrame(() => { wheelRotation.value = nextRotation; });
	later(() => {
		spinning.value = false;
		settling.value = true;
		later(() => emit('reveal', props.target.name), reducedMotion ? 30 : 520);
	}, durations.wheel + 80);
};

const spinSlot = (): void => {
	if( spinning.value ) return;
	spinning.value = true;
	slotLocked.value = false;
	const finalIndex = slotTargetIndex(visibleCategories.value, slotIndex.value, reducedMotion);
	nextTick(() => window.requestAnimationFrame(() => { slotIndex.value = finalIndex; }));
	later(() => {
		spinning.value = false;
		slotLocked.value = true;
		later(() => emit('reveal', props.target.name), durations.slotSettle);
	}, durations.slot + 80);
};

onBeforeUnmount(clearTimers);
</script>

<template>
	<section class="category-selector-shell" :class="`category-selector-${mode}`">
		<div>
			<h1 v-if="mode === 'cards'">{{ labels.flipCardTitle }}</h1>
			<h1 v-else-if="mode === 'wheel'">{{ labels.spinCategoryTitle }}</h1>
			<h1 v-else>{{ labels.pullCategoryTitle }}</h1>
			<p v-if="mode === 'cards'" class="category-selector-helper">{{ labels.flipCardHelp }}</p>
			<p v-else-if="mode === 'wheel'" class="category-selector-helper">{{ labels.spinCategoryHelp }}</p>
			<p v-else class="category-selector-helper">{{ labels.pullCategoryHelp }}</p>
		</div>

		<div class="category-selector-stage">
			<div v-if="mode === 'cards'" class="mystery-card-row" :class="selectedCard === null ? '' : `has-selection selected-${selectedCard}`" aria-label="Mystery card choices">
				<button
					v-for="(_, index) in visibleCategories"
					:key="index"
					type="button"
					class="mystery-card"
					:class="{
						'is-selected': selectedCard === index,
						'is-dimmed': selectedCard !== null && selectedCard !== index,
						'is-flipped': selectedCard === index && cardFlipped,
					}"
					:disabled="selectedCard !== null"
					:aria-label="`Choose mystery card ${index + 1}`"
					@click="chooseCard(index)"
				>
					<span class="mystery-card-inner">
						<span class="mystery-card-face mystery-card-back" :style="cardBackStyle">
							<img v-if="!settings.cardImageUrl && logoUrl" :src="logoUrl" alt="">
							<strong v-else-if="!settings.cardImageUrl">{{ gameTitle }}</strong>
						</span>
						<span class="mystery-card-face mystery-card-front" :style="cardFrontStyle">
							<span><small>{{ labels.category }}</small><strong>{{ target.name }}</strong></span>
						</span>
					</span>
				</button>
			</div>

			<div v-else-if="mode === 'wheel'">
				<div class="category-wheel-stage" aria-label="Category Prize Wheel">
					<div class="category-wheel-pointer" :class="{ ticking: spinning, settling }" />
					<div class="category-wheel-wrap" :class="{ spinning }" :style="{ transform: `rotate(${wheelRotation}deg)`, transitionDuration: `${durations.wheel}ms` }">
						<svg class="category-wheel" viewBox="-110 -110 220 220" role="img" aria-label="Prize Wheel category selector">
							<template v-for="slice in wheelSlices" :key="slice.key">
								<path :d="slice.path" :fill="slice.color" />
								<text :x="slice.labelX" :y="slice.labelY" :fill="settings.wheelFontColor" :transform="`rotate(${slice.labelRotation} ${slice.labelX} ${slice.labelY})`">{{ slice.name }}</text>
							</template>
							<circle class="category-wheel-center" cx="0" cy="0" r="25" />
							<circle cx="0" cy="0" r="13" :fill="theme.primaryButtonBackground" />
						</svg>
					</div>
				</div>
				<p class="category-spin-note">{{ labels.tapSpinCategory }}</p>
			</div>

			<div v-else>
				<div class="category-drum-stage" :class="{ locked: slotLocked }" :style="slotStageStyle">
					<div class="category-drum-window" :style="slotWindowStyle">
						<div class="category-drum-strip" :style="{ transform: slotTransform, transition: slotTransition }">
							<div
								v-for="(category, index) in slotRows"
								:key="category.key"
								class="category-drum-row"
								:class="{ active: index === slotIndex, near: Math.abs(index - slotIndex) === 1 }"
								:style="{ color: settings.slotFontColor }"
							>
								{{ category.name }}
							</div>
						</div>
					</div>
				</div>
				<p class="category-spin-note">{{ labels.tapSpinCategory }}</p>
			</div>
		</div>

		<button v-if="mode === 'cards'" ref="revealButton" type="button" class="primary-action category-selector-action" :class="{ hidden: !cardReady }" @click="emit('reveal', target.name)">{{ labels.revealQuestion }}</button>
		<button v-else-if="mode === 'wheel'" type="button" class="primary-action category-selector-action" :disabled="spinning" @click="spinWheel">{{ labels.spin }}</button>
		<button v-else type="button" class="primary-action category-selector-action" :disabled="spinning" @click="spinSlot">{{ labels.spin }}</button>
	</section>
</template>
